/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import { Fragment, useRef, useState, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import { useSelector } from 'react-redux';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import PropTypes from 'prop-types';

// Component imports
import { Button, Col, Form, Input, List, Popover, Row, Tooltip, Typography } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import BotAvatar from '../UI/BotAvatar';
import CreateBotModal from '../Modals/CreateBotModal';

// Misc imports
import is from '../../utils/is';
import useUserPermissions from '../../utils/hooks/useUserPermissions';
import replaceUrlParams from '../../utils/string/replaceUrlParams';
import { NAV_BOT, NAV_BOTS } from '../../constants/navigation';
import { useFetchBotsQuery } from '../../store/bots/botsApi';
import { PERMISSIONS_ORG, BOTS_TYPES } from '../../constants/main';

const BotSelector = forwardRef((props, ref) => {
  const { children, popoverProps, parentElement } = props;
  const navigate = useNavigate();
  const location = useLocation();
  const { orgId } = useParams();
  const [botSearchForm] = Form.useForm();
  const { t } = useTranslation();
  const botSearchRef = useRef(null);
  const userPermissions = useUserPermissions(orgId, null);
  const anchorEl = typeof parentElement === 'function' ? parentElement() : parentElement;

  const { currentUser } = useSelector((state) => state.auth);
  const { recentBots: allRecentBots } = useSelector((state) => state.bots);
  const { data: bots, isFetching: isFetchingBots, refetch: fetchBots } = useFetchBotsQuery({ orgId }, { skip: !orgId || !(is.array(userPermissions) && !is.emptyArray(userPermissions) && userPermissions.some(permission => Object.values(PERMISSIONS_ORG).includes(permission))) });

  const [, updateState] = useState(); // Hook to force a component re-render
  const [isBotsPopoverOpen, setIsBotsPopoverOpen] = useState(false);
  const [isCreateBotModalOpen, setIsCreateBotModalOpen] = useState(false);

  const currentOrg = (currentUser?.organizations || []).find(org => org.id === orgId);

  const botSearch = botSearchForm.getFieldValue('botSearch');
  const botsDatasource = botSearch ? (bots?.items ? [...bots.items] : [])
    .filter(bot => bot.displayName.toLowerCase().includes(botSearch.toLowerCase())) : (bots?.items ? [...bots.items] : []);
  const recentBots = botSearch ? allRecentBots.filter(bot => bot.displayName.toLowerCase().includes(botSearch.toLowerCase())) : allRecentBots;

  useEffect(() => {
    botSearchForm.resetFields();
  }, [currentOrg]);

  useImperativeHandle(ref, () => ({
    // Open the popover programmatically
    openPopover: () => {
      setIsBotsPopoverOpen(true);
      setTimeout(() => {
        botSearchRef.current?.focus();
      }, 150);
    }
  }));

  const onArrowPress = useCallback((e) => {
    if (!isBotsPopoverOpen) {
      return;
    }

    // Only prevent default if the user is pressing an arrow key or enter
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown' || e.key === 'ArrowLeft' || e.key === 'ArrowRight' || e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
    } else {
      return;
    }

    // Capture the key and init the index
    const { key } = e;
    let index = -1;

    if (isBotsPopoverOpen) {
      /*
      * We keep track of the separate lists to make the
      * transition between the two categories possible
      */
      const recentBotElements = document.querySelectorAll('#recentBots .bot-item');
      const allBotElements = document.querySelectorAll('#allBots .bot-item');
      const botItems = [...recentBotElements, ...allBotElements];

      index = Array.from(botItems).findIndex(el => el.classList.contains('selected-bot-item'));
      if (index >= 0) {
        botItems[index].classList.remove('selected-bot-item');
      } else if (key === 'Enter') {
        return;
      }

      switch (key) {
        case 'ArrowUp':
          if (index === -1) {
            index = 0;
          } else if (botItems[index] === allBotElements[0]) {
            index -= (recentBotElements.length % 2 === 0) ? 2 : 1;
          } else {
            index -= 2;
          }
          break;
        case 'ArrowDown':
          if (index === -1) {
            index = 0;
          } else if (
            botItems[index] === recentBotElements[recentBotElements.length - 2]
            || botItems[index] === recentBotElements[recentBotElements.length - 1]
          ) {
            index += (recentBotElements.length % 2 === 0) ? 2 : 1;
          } else {
            index += 2;
          }
          break;
        case 'ArrowLeft':
          index -= 1;
          break;
        case 'ArrowRight':
          index += 1;
          break;
        default:
          break;
      }
      // We don't allow negative selections
      if (index < 0) {
        index = 0;
      }
      // No selection over item count either
      if (index >= botItems.length ) {
        index = botItems.length - 1;
      }
      botItems[index]?.classList.add('selected-bot-item');
      botItems[index]?.scrollIntoView({ block: 'center' });
      // On enter select the item and close the popup
      if (key === 'Enter') {
        setIsBotsPopoverOpen(false);
        document.querySelector('.selected-bot-item a')?.click();
      }
    }
  }, [isBotsPopoverOpen]);

  useEffect(() => {
    window.addEventListener('keydown', onArrowPress);
    return () => {
      window.removeEventListener('keydown', onArrowPress);
    };
  }, [isBotsPopoverOpen]);

  /**
   * Method to render bots list
   * @param {String} botsType should be either 'recent' or 'all'
   * @returns
   */
  const renderBotsList = (botsType) => {
    const title = botsType === BOTS_TYPES.ALL ? t('All Agents') : t('Recent Agents');
    const botsList = botsType === BOTS_TYPES.ALL ? botsDatasource : recentBots;
    const isLoading = botsType === BOTS_TYPES.ALL ? (isFetchingBots && !bots) : false;

    return (
      <Col span={24} id={botsType === BOTS_TYPES.ALL ? 'allBots' : 'recentBots'}>
        <Typography.Text strong>{title}</Typography.Text>

        <List
          grid={{ column: 2 }}
          dataSource={botsList}
          loading={isLoading}
          className="mt-3"
          renderItem={bot => (
            <List.Item className="mb-2 bot-item">
              <Tooltip
                overlayClassName={classNames('pointer-events-none', { 'd-none': !isBotsPopoverOpen })}
                placement="top" title={bot.displayName}
              >
                {
                  !location.pathname.includes(bot.id)
                    ? <Link to={replaceUrlParams(NAV_BOT, { tenantId: bot.id, orgId }) }>
                      <Button
                        type="text"
                        onClick={() => {
                          setIsBotsPopoverOpen(false);
                        }}
                        block
                        className="d-flex px-2 fw-normal"
                      >
                        <BotAvatar bot={bot} avatarSize={22} className="align-items-left d-flex" />
                      </Button>
                    </Link>
                    : <Button
                      type="text"
                      onClick={() => {
                        setIsBotsPopoverOpen(false);
                      }}
                      block
                      className="d-flex px-2 fw-normal"
                    >
                      <BotAvatar bot={bot} avatarSize={22} className="align-items-left d-flex" />
                    </Button>
                }

              </Tooltip>
            </List.Item>
          )}
        />
      </Col>
    );
  };

  const renderBotsMenu = () => {
    return (
      <Row>
        <Col span={24}>
          <Form.Item
            name="botSearch"
            className="mb-0"
          >
            <Input
              ref={botSearchRef}
              size="large"
              autoComplete="off"
              placeholder={t('Search Agent')}
              onChange={() => document.querySelector('.selected-bot-item')?.classList.remove('selected-bot-item')}
              prefix={<SearchOutlined className="text-secondary" />}
              onKeyDown={(e) => {
                if (e.key.includes('Arrow')) {
                  onArrowPress(e);
                }
              }}
              allowClear
            />
          </Form.Item>
        </Col>

        <Col span={12} className="my-4">
          <Typography.Link
            onClick={() => {
              setIsBotsPopoverOpen(false);
              navigate(replaceUrlParams(NAV_BOTS, { orgId }));
            }}
            strong
          >
            {t('View All')}
          </Typography.Link>
        </Col>

        <Col span={12} className="my-4 text-right">
          <Typography.Link
            onClick={() => {
              setIsBotsPopoverOpen(false);
              setIsCreateBotModalOpen(true);
            }}
            strong
          >
            {t('Create new Agent')}
          </Typography.Link>
        </Col>

        {
          recentBots && !is.emptyArray(recentBots) ? (
            renderBotsList(BOTS_TYPES.RECENT)
          ) : null
        }

        {
          renderBotsList(BOTS_TYPES.ALL)
        }
      </Row>
    );
  };

  const closeCreateModal = () => {
    setIsCreateBotModalOpen(false);
  };

  return (
    <Fragment>
      <Form
        name="botSearchForm"
        form={botSearchForm}
        onFieldsChange={(_, allFields) => {
          updateState(allFields);
        }}
      >
        <Popover
          content={renderBotsMenu}
          destroyTooltipOnHide
          overlayInnerStyle={{ maxWidth: 500, maxHeight: 365, marginTop: -16, overflowY: 'scroll' }}
          overlayClassName="bots-menu-popup"
          showArrow={false}
          onOpenChange={isOpen => {
            setIsBotsPopoverOpen(isOpen);
            if (isOpen) {
              fetchBots();
              document.querySelector('.selected-bot-item')?.classList.remove('selected-bot-item');
              setTimeout(() => {
                botSearchRef.current.focus();
              }, [150]);
            }
          }}
          trigger="click"
          open={isBotsPopoverOpen}
          {...anchorEl ? { getPopupContainer: () => anchorEl } : {}}
          {...popoverProps}
        >
          {children}
        </Popover>
      </Form>

      <CreateBotModal isModalOpen={isCreateBotModalOpen} closeModal={closeCreateModal} />
    </Fragment>
  );
});

BotSelector.propTypes = {
  children: PropTypes.node,
  popoverProps: PropTypes.object,
  parentElement: PropTypes.oneOfType([PropTypes.object, PropTypes.func])
};

export default BotSelector;
